import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { EmptyState } from "../components/shared/EmptyState";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { SystemDetail } from "../components/shared/SystemDetail";
import { toast } from "sonner";
import { Boxes, Search, FileText } from "lucide-react";

const STATUS = {
  GENERATED: "text-slate-300 border-slate-500/30 bg-slate-500/10",
  DRAFT: "text-slate-400 border-slate-500/30 bg-slate-500/10",
  CONFIGURATION_REQUIRED: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  NEEDS_REVIEW: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  APPROVED: "text-cyan-400 border-cyan-500/30 bg-cyan-500/10",
  ACTIVE: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  IN_TEST: "text-orange-400 border-orange-500/30 bg-orange-500/10",
  VERIFIED: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  NEEDS_ADJUSTMENT: "text-orange-400 border-orange-500/30 bg-orange-500/10",
  ARCHIVED: "text-slate-500 border-slate-500/20 bg-slate-500/5",
};
const STATUS_LABEL = {
  GENERATED: "Generado", DRAFT: "Generado", CONFIGURATION_REQUIRED: "Config. requerida",
  APPROVED: "Aprobado", ACTIVE: "Activo", IN_TEST: "En prueba", VERIFIED: "Verificado",
  NEEDS_ADJUSTMENT: "Necesita ajustes", ARCHIVED: "Archivado", NEEDS_REVIEW: "Para revisar",
};
const FILTERS = ["TODOS", "GENERATED", "APPROVED", "ACTIVE", "VERIFIED"];

export default function Systems() {
  const navigate = useNavigate();
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("TODOS");
  const [open, setOpen] = useState(null);

  const load = () => api.get("/systems").then(({ data }) => setSystems(data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try { await api.post(`/systems/${id}/${action}`); toast.success(action === "approve" ? "Aprobado" : "Archivado"); await load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const filtered = systems.filter((s) =>
    (filter === "TODOS" || s.status === filter) &&
    (s.title || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold text-white">Delegation Systems</h1>
        <p className="mt-1 text-slate-400">Sistemas construidos por Architect. Revisa y aprueba antes de activarlos.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input data-testid="systems-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar sistema…" className="bg-white/[0.03] border-white/10 pl-9" />
        </div>
        <div className="flex gap-1 rounded-xl bg-white/[0.03] p-1">
          {FILTERS.map((f) => <button key={f} data-testid={`systems-filter-${f}`} onClick={() => setFilter(f)} className={`shrink-0 rounded-lg px-3 py-2.5 sm:py-1.5 text-xs font-mono ${filter === f ? "bg-amber-500 text-black" : "text-slate-400 hover:text-white"}`}>{f}</button>)}
        </div>
      </div>

      {loading ? <div className="mono-label text-slate-500">CARGANDO…</div> :
        filtered.length === 0 ? <EmptyState icon={Boxes} title="Sin sistemas todavía" description="Crea un proyecto y ejecuta Architect para generar tu primer Delegation System." action={<Button onClick={() => navigate("/app/projects")} className="bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold">Ir a proyectos</Button>} /> :
        <div className="grid sm:grid-cols-2 gap-5">
          {filtered.map((s) => (
            <div key={s.id} className="card-arch p-5" data-testid={`system-card-${s.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0"><FileText className="h-4 w-4 text-cyan-400 shrink-0" /><h3 className="font-medium text-white truncate">{s.title}</h3></div>
                <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[0.6rem] font-mono ${STATUS[s.status] || STATUS.GENERATED}`}>{STATUS_LABEL[s.status] || s.status}</span>
              </div>
              <p className="mt-2 text-sm text-slate-400 line-clamp-2">{s.objective}</p>
              <div className="mt-4 flex gap-2">
                <Button data-testid={`system-card-sop-view-btn-${s.id}`} onClick={() => navigate(`/app/systems/${s.id}`)} className="bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold flex-1">Abrir sistema</Button>
                {s.status !== "ARCHIVED" && <Button onClick={() => act(s.id, "archive")} variant="ghost" className="text-slate-400 hover:text-red-400 rounded-full">Archivar</Button>}
              </div>
            </div>
          ))}
        </div>}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="bg-[#0B0D13] border-white/10 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head text-white">{open?.title}</DialogTitle>
            <DialogDescription className="text-slate-500">Procedimiento, derechos de decisión y verificación del sistema.</DialogDescription>
          </DialogHeader>
          <SystemDetail sys={open} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
